import React, { useCallback, useEffect, useState } from "react";
import { Button, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import clsx from "clsx";

import AddressForm, {
  AddressFormWayPoint,
} from "../components/order/AddressForm";
import FreightInfo from "../components/order/FreightInfo";
import RequestNote from "../components/order/RequestNote";
import FreightOption from "../components/order/FreightOption";
import TodayOrder from "../components/order/TodayOrder";
import Summary from "../components/order/Summary";
import OrderCheck from "../components/order/OrderCheck";
import Popup from "../components/order/Popup"; // for popup
import BusinessHoursPopup from "../components/order/BusinessHoursPopup";
import OrderService from "../services/order";
import Config from "../services/config";
import {
  axle,
  changeDocumentTitle,
  getVehicleTypes,
  getVehicleWeights,
  motorcycle,
  validateAddress,
  validatePhone,
  isOutsideBusinessHours,
} from "../lib";
import OrdererService from "../services/orderer";
import InfoBoxService from "../services/info_box";
import {IInfoBox} from "../interfaces/info_box";

export const border = `solid 1px #ebedf8`;

const useStyles = makeStyles((theme) => ({
  left: {
    width: 480,
    border,
    backgroundColor: "white",
  },
  right: {
    border,
    width: 610,
    backgroundColor: "white",
  },
  sidebar: {
    width: 223,
    marginLeft: 20,
  },
  content: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 40,
    paddingRight: 36,
  },
  waypoint: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 180,
    paddingRight: 36,
  },
  leftContent: {
    paddingTop: 45,
  },
  rightContent: {
    paddingTop: 0,
  },
  topContent: {
    marginTop: -6,
  },
  sidebarContent: {
    paddingTop: 13,
    paddingBottom: 12,
    paddingLeft: 19,
    paddingRight: 19,
  },
  todayOrder: {
    border,
    backgroundColor: "white",
  },
  summary: {
    marginTop: 17,
    paddingTop: 25,
    border,
    backgroundColor: "white",
  },

  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  loginImage: {
    width: 400,
    height: 100,
  },
}));

const motorcycleWeight = 0.02;

const Left = () => {
  const classes = useStyles();
  const [latestAddresses, setLatestAddresses] = useState<any>([]);
  const [waypoint, setWaypoint] = useState<any>([]);

  const navigate = useNavigate();

  const inquiryLatestAddresses = useCallback(async () => {
    try {
      const orderService = new OrderService(navigate);
      const response = await orderService.inquiryLatestAddresses();
      if (response.data.addresses) {
        setLatestAddresses(response.data.addresses);
      }
    } catch (e) {
      console.error("failed to handle inquiry latest orders:", e);
    }
  }, [setLatestAddresses, navigate]);

  useEffect(() => {
    queueMicrotask(inquiryLatestAddresses);
  }, [inquiryLatestAddresses]);

  const { watch, setValue } = useFormContext();
  return (
    <Grid container direction={"column"}>
      <Grid item className={classes.content}>
        <AddressForm
          title={"출발지 정보"}
          name={"departure"}
          latestAddresses={latestAddresses}
        />
      </Grid>
      <Grid item className={classes.waypoint}>
        <Button
          disabled={watch("waypoint0") && watch("waypoint1")}
          sx={{ p: 1 }}
          variant="contained"
          endIcon={<AddCircleOutlineIcon />}
          onClick={() => {
            if (watch("waypoint0")) setValue("waypoint1", {});
            else setValue("waypoint0", {});
          }}
        >
          경유지 추가
        </Button>
      </Grid>
      {watch("waypoint0") && (
        <Grid item className={classes.content}>
          <AddressFormWayPoint
            name={`waypoint0`}
            waypoint={waypoint}
            setWaypoint={setWaypoint}
          />
        </Grid>
      )}

      {watch("waypoint1") && (
        <Grid item className={classes.content}>
          <AddressFormWayPoint
            name={`waypoint1`}
            waypoint={waypoint}
            setWaypoint={setWaypoint}
          />
        </Grid>
      )}

      <Grid item className={clsx(classes.content, classes.leftContent)}>
        <AddressForm
          title={"도착지 정보"}
          name={"arrival"}
          latestAddresses={latestAddresses}
        />
      </Grid>
    </Grid>
  );
};

const Right = (props: any) => {
  const {
    setConfigIsCheckTotalWeight,
    configIsInputTotalLength,
    setConfigIsInputTotalLength,
  } = props;
  const classes = useStyles();
  const [configVehicleTypes, setConfigVehicleTypes] = useState<string[]>([""]);
  const [configVehicleWeight, setConfigVehicleWeight] = useState<number[]>([0]);
  const [selectedVehicleTypeInList, setSelectedVehicleTypeInList] =
    useState<string>("");
  const [selectedVehicleWeightInList, setSelectedVehicleWeightInList] =
    useState<number>(0);

  const [configItem, setConfigItem] = useState<string>("");
  const [configRequestNoteForDriver, setConfigRequestNoteForDriver] =
    useState<string>("");
  const [configRequestNoteForAdmin, setConfigRequestNoteForAdmin] =
    useState<string>("");

  const [configLoadingType, setConfigLoadingType] = useState<string>("");
  const [configUnloadingType, setConfigUnloadingType] = useState<string>("");

  const { setValue } = useFormContext();
  const navigate = useNavigate();

  const getConfig = useCallback(async () => {
    try {
      const configService = new Config(navigate);
      const config = await configService.inquiry();

      if (config) {
        const allVehicleTypes = getVehicleTypes();
        const allVehicleWeights = getVehicleWeights();

        setValue("packagingType", config.packagingType);
        setValue("loadingType", config.loadingType);
        setValue("unloadingType", config.unloadingType);
        setValue("optionMixedLoading", config.optionMixedLoading);

        config.vehicleTypes = config.vehicleTypes.filter((v: string) =>
          allVehicleTypes.includes(v)
        );
        const vehicleTypeList = allVehicleTypes.filter(
          (v: string) => !config.vehicleTypes.includes(v)
        );

        config.vehicleWeights = config.vehicleWeights.filter((v: number) =>
          allVehicleWeights.includes(v)
        );
        const vehicleWeightList = allVehicleWeights.filter(
          (v: number) => !config.vehicleWeights.includes(v)
        );

        const newVehicleTypes = [
          ...config.vehicleTypes,
          ...vehicleTypeList,
        ].filter((v: any) => getVehicleTypes().includes(v));
        const newVehicleWeights = [
          ...config.vehicleWeights,
          ...vehicleWeightList,
        ].filter((v: any) => getVehicleWeights().includes(v));
        setConfigVehicleTypes(newVehicleTypes);
        setConfigVehicleWeight(newVehicleWeights);

        const showFixedListCount = 4;
        setSelectedVehicleTypeInList(newVehicleTypes[showFixedListCount]);
        setSelectedVehicleWeightInList(newVehicleWeights[showFixedListCount]); //Type Casting

        setConfigItem(config.item);
        setConfigLoadingType(config.loadingType);
        setConfigUnloadingType(config.unloadingType);
        setConfigRequestNoteForDriver(config.requestNoteForDriver);
        setConfigRequestNoteForAdmin(config.requestNoteForAdmin);
        setConfigIsCheckTotalWeight(config.isCheckTotalWeight);
        setConfigIsInputTotalLength(config.isInputTotalLength);
      }
    } catch (e) {
      console.error("failed to handle get config:", e);
    }
  }, [
    setConfigVehicleTypes,
    setConfigVehicleWeight,
    setConfigItem,
    setConfigRequestNoteForDriver,
    setConfigRequestNoteForAdmin,
    navigate,
    setConfigIsCheckTotalWeight,
    setConfigIsInputTotalLength,
    setValue,
  ]);

  useEffect(() => {
    queueMicrotask(getConfig);
  }, [getConfig]);

  return (
    <Grid container direction={"column"}>
      <Grid item className={classes.content}>
        <FreightInfo
          configVehicleTypes={configVehicleTypes}
          selectedVehicleTypeInList={selectedVehicleTypeInList}
          setSelectedVehicleTypeInList={setSelectedVehicleTypeInList}
          configVehicleWeight={configVehicleWeight}
          selectedVehicleWeightInList={selectedVehicleWeightInList}
          setSelectedVehicleWeightInList={setSelectedVehicleWeightInList}
          configItem={configItem}
          configLoadingType={configLoadingType}
          configUnloadingType={configUnloadingType}
          configIsInputTotalLength={configIsInputTotalLength}
        />
      </Grid>
      <Grid item className={clsx(classes.content, classes.rightContent)}>
        <RequestNote
          requestNoteForDriver={configRequestNoteForDriver}
          requestNoteForAdmin={configRequestNoteForAdmin}
        />
      </Grid>
      <Grid
        item
        className={clsx(
          classes.content,
          classes.rightContent,
          classes.topContent
        )}
      >
        <FreightOption />
      </Grid>
    </Grid>
  );
};

const Sidebar = () => {
  const classes = useStyles();
  const [todayOrderCount, setTodayOrderCount] = useState<number>(0);

  const navigate = useNavigate();
  const { watch, setValue } = useFormContext(); //하위 form 들에 있는 값을 가지고 오기 위해 사용

  const estimate = useCallback(async () => {
    try {
      const departure = watch("departure.addressBase");
      const arrival = watch("arrival.addressBase");
      const vehicleType = watch("vehicleType");
      const vehicleWeight = watch("vehicleWeight");
      const totalLength = watch("totalLength");
      const optionMixedLoading = watch("optionMixedLoading");

      const params = {
        departure,
        arrival,
        vehicleType,
        vehicleWeight,
        totalLength,
        optionMixedLoading,
      };

      const orderService = new OrderService(navigate);
      const response = await orderService.estimate(params);

      const distance = response.data.distance / 1000;
      const hour = response.data.duration / 60;
      const minute = response.data.duration % 60;

      let duration = "";
      if (hour > 0) {
        duration += `${Math.floor(hour)}시간 ${minute.toFixed(0)}분`;
      } else {
        duration += `${minute}분`;
      }

      setValue("estimatedDistance", distance.toFixed(1));
      setValue("estimatedDuration", duration);
      setValue("estimatedCharge", response.data.charge);
    } catch (e) {
      console.error("failed to estimate distance and duration:", e);
    }
  }, [navigate, setValue, watch]);

  const inquiryTodayOrder = useCallback(async () => {
    try {
      const orderService = new OrderService(navigate);
      const response = await orderService.inquiryTodayCount();
      setTodayOrderCount(response.data.count);
    } catch (e) {
      console.error("failed to inquiry today orders:", e);
    }
  }, [setTodayOrderCount, navigate]);

  useEffect(() => {
    queueMicrotask(inquiryTodayOrder);
  }, [inquiryTodayOrder]);

  return (
    <Grid container direction={"column"}>
      <Grid item className={clsx(classes.sidebarContent, classes.todayOrder)}>
        <TodayOrder todayOrderCount={todayOrderCount} />
      </Grid>
      <Grid item className={clsx(classes.sidebarContent, classes.summary)}>
        <Summary estimate={estimate} />
      </Grid>
    </Grid>
  );
};

export const checkLengthValidate = (
  vehicleType: string,
  vehicleWeight: number,
  totalLength: number
) => {
  if (vehicleType === "다마스" && totalLength > 2) return false;
  else if (vehicleType === "라보" && totalLength > 2.5) return false;
  else if (vehicleWeight === 1 && totalLength > 4) return false;
  else if (vehicleWeight === 1.4 && totalLength > 4.2) return false;
  else if (vehicleWeight === 2.5 && totalLength > 5.5) return false;
  else if (vehicleWeight === 3.5 && totalLength > 6) return false;
  else if (
    vehicleWeight === 5 &&
    totalLength > 8 &&
    !axle.includes(vehicleType)
  )
    return false;
  else if (
    vehicleWeight === 5 &&
    totalLength > 11 &&
    axle.includes(vehicleType)
  )
    return false;
  else if (vehicleWeight === 11 && totalLength > 11) return false;
  else if (vehicleWeight === 25 && totalLength > 11) return false;

  return true;
};

export const compareWithoutPackagingTypePtWithItem = (item: string) => {
  return (
    item.includes("PT") ||
    item.includes("파렛") ||
    item.includes("파레트") ||
    item.includes("빠레") ||
    item.includes("빠렛")
  );
};

export const checkTotalLengthWithVehicleLength = (
  vehicleType: string,
  vehicleWeight: number,
  length: number
) => {
  if (!checkLengthValidate(vehicleType, vehicleWeight, length)) {
    if (
      !window.confirm(
        `총길이를 한번 더 확인해주세요. \n정말 ${length}m로 진행하시겠습니까?`
      )
    )
      return false;
  }
  return true;
};

export default function Order() {
  const classes = useStyles();
  const { register } = useForm();
  const [sendingOrder, setSendingOrder] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [configIsCheckTotalWeight, setConfigIsCheckTotalWeight] =
    useState<boolean>(false);
  const [configIsInputTotalLength, setConfigIsInputTotalLength] =
    useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false); // for popup
  const [showBusinessHoursPopup, setShowBusinessHoursPopup] = useState<boolean>(false);
  const [orderer, setOrderer] = useState<any>({ name: "", phone: "" });
  const [infoBox, setInfoBox] = useState<IInfoBox>({
    title: '', content: '', isOpen: false,
  })

  const methods = useForm();
  const navigate = useNavigate();

  const getOrderer = useCallback(async () => {
    try {
      const ordererService = new OrdererService(navigate);
      const response = await ordererService.getOrdererInfo();
      setOrderer(response.data);
    } catch (e) {
      console.error("failed to handle orderer:", e);
    }
  }, [setOrderer, navigate]);

  useEffect(() => {
    changeDocumentTitle("운송주문");
    queueMicrotask(getOrderer);
    register("departure");
  }, [getOrderer, register]);

  const checkWeightValidate = (
    vehicleType: string,
    vehicleWeight: string,
    totalWeight: number
  ) => {
    if (vehicleType === "다마스" && totalWeight > 0.5) return false;
    else if (vehicleType === "라보" && totalWeight > 0.7) return false;
    else if (vehicleWeight === "1" && totalWeight > 2) return false;
    else if (vehicleWeight === "1.4" && totalWeight > 2.2) return false;
    else if (vehicleWeight === "2.5" && totalWeight > 5) return false;
    else if (vehicleWeight === "3.5" && totalWeight > 6) return false;
    else if (
      vehicleWeight === "5" &&
      totalWeight > 10 &&
      !axle.includes(vehicleType)
    )
      return false;
    else if (
      vehicleWeight === "5" &&
      totalWeight > 20 &&
      axle.includes(vehicleType)
    )
      return false;
    else if (vehicleWeight === "11" && totalWeight > 20) return false;
    else if (vehicleWeight === "25" && totalWeight > 30) return false;

    return true;
  };

  const checkOnlyTotalWeight = (weight: number) => {
    if (weight > 30) {
      if (
        !window.confirm(
          `총중량을 한번 더 확인해주세요. \n정말 ${weight} 톤으로 진행하시겠습니까?`
        )
      )
        return false;
    }
    return true;
  };

  const checkTotalWeightWithVehicleWeight = (
    vehicleType: string,
    vehicleWeight: string,
    weight: number
  ) => {
    if (!checkWeightValidate(vehicleType, vehicleWeight, weight)) {
      if (
        !window.confirm(
          `총중량을 한번 더 확인해주세요. \n정말 ${weight} 톤으로 진행하시겠습니까?`
        )
      )
        return false;
    }
    return true;
  };

  const checkTotalWeight = (
    weight: number,
    vehicleType: string,
    vehicleWeight: string
  ) => {
    if (configIsCheckTotalWeight) {
      return checkOnlyTotalWeight(weight);

    } else {
      return checkTotalWeightWithVehicleWeight(vehicleType, vehicleWeight, weight);

    }
  };

  const validate = (data: any): boolean => {
    if (data.departure.addressBase === "") {
      alert("출발지 주소를 입력해주세요.");
      return false;
    }

    if (data.arrival.addressBase === "") {
      alert("도착지 주소를 입력해주세요.");
      return false;
    }

    if (
      methods.watch("waypoint0") &&
      methods.watch("waypoint0").addressBase === ""
    ) {
      alert("첫번 째 경유지 주소를 입력해주세요.");
      return false;
    }

    if (
      methods.watch("waypoint1") &&
      methods.watch("waypoint1").addressBase === ""
    ) {
      alert("두번 째 경유지 주소를 입력해주세요.");
      return false;
    }

    if (
      data?.waypoint0?.date?.getTime() <=
      data.departure.date.getTime() + 10000
    ) {
      alert("경유지 도착시간은 출발지 상차시간보다 뒤에 있어야 합니다.");
      return false;
    }

    if (
      data?.waypoint1?.date?.getTime() <=
        data?.waypoint0?.date?.getTime() + 10000 ||
      data?.waypoint1?.date?.getTime() <=
        data?.departure?.date?.getTime() + 10000
    ) {
      alert(
        "경유지 도착시간은 상차지나 이전 경유지 도착시간보다 뒤에 있어야 합니다."
      );
      return false;
    }

    if (
      data.arrival.date.getTime() <= data.departure.date.getTime() + 10000 ||
      data.arrival.date.getTime() <= data?.waypoint0?.date?.getTime() + 10000 ||
      data.arrival.date.getTime() <= data?.waypoint1?.date?.getTime() + 10000
    ) {
      alert("하차시간이 상차시간이나 경유지시간보다 뒤에 있어야 합니다.");
      return false;
    }

    if (!validatePhone(data.departure.phone)) {
      alert("출발지 연락처 형식이 올바르지 않습니다.");
      return false;
    }

    if (!validatePhone(data.arrival.phone)) {
      alert("도착지 연락처 형식이 올바르지 않습니다.");
      return false;
    }

    if (
      !validateAddress(data.departure.addressBase, data.arrival.addressBase)
    ) {
      alert("출발지와 도착지의 주소가 동일합니다.");
      return false;
    }

    if (data.totalWeight === 0) {
      alert(
        `중량 형식이 올바르지 않습니다. 0보다 큰 수를 입력해주세요\n예) 0.1, 1, 11, 11.5`
      );
      return false;
    }

    if (
      !checkTotalWeight(
        data.totalWeight,
        data.vehicleType,
        String(data.vehicleWeight)
      )
    )
      return false;

    if (
      configIsInputTotalLength &&
      !checkTotalLengthWithVehicleLength(
        data.vehicleType,
        data.vehicleWeight,
        data.totalLength
      )
    )
      return false;

    if (data.packagingType !== "pt")
      if (compareWithoutPackagingTypePtWithItem(data.item)) {
        return window.confirm(
          "포장방식이 파레트가(PT)가 아니지만 품목에 관련 단어가 들어있습니다.\n계속 진행하시겠습니까?"
        );
      }

    return true;
  };

  const trimData = (data: any) => {
    data.departure.addressBase = data.departure.addressBase.trim();
    data.departure.addressDetail = data.departure.addressDetail.trim();
    data.departure.name = data.departure.name.trim();
    data.departure.phone = data.departure.phone.trim();

    data.arrival.addressBase = data.arrival.addressBase.trim();
    data.arrival.addressDetail = data.arrival.addressDetail.trim();
    data.arrival.name = data.arrival.name.trim();
    data.arrival.phone = data.arrival.phone.trim();

    if (data?.waypoint0?.name) {
      data.waypoint0.addressBase = data.waypoint0.addressBase.trim();
      data.waypoint0.addressDetail = data.waypoint0.addressDetail.trim();
      data.waypoint0.name = data.waypoint0.name.trim();
      data.waypoint0.phone = data.waypoint0.phone.trim();
    }

    if (data?.waypoint1?.name) {
      data.waypoint1.addressBase = data.waypoint1.addressBase.trim();
      data.waypoint1.addressDetail = data.waypoint1.addressDetail.trim();
      data.waypoint1.name = data.waypoint1.name.trim();
      data.waypoint1.phone = data.waypoint1.phone.trim();
    }

    data.item = data.item.trim();
    data.requestNoteForAdmin = data.requestNoteForAdmin.trim();
    data.requestNoteForDriver = data.requestNoteForDriver.trim();
  };

  const onSubmit = async (data: any) => {
    // Check if outside business hours
    if (isOutsideBusinessHours()) {
      setShowBusinessHoursPopup(true);
    }

    trimData(data);
    // radio button 에서 value 를 string 으로 캐스팅해서 사용하기 때문에 여기에서 다시 float 으로 바꿔줌
    if (data.vehicleType === motorcycle) {
      data.vehicleWeight = motorcycleWeight;
      data.totalWeight = motorcycleWeight;
    } else {
      data.vehicleWeight = parseFloat(data.vehicleWeight);
    }

    const weight = parseFloat(data.totalWeight);

    if (isNaN(weight) || weight === 0) {
      data.totalWeight = 0;
    } else {
      data.totalWeight = weight;
    }

    const length = parseFloat(data.totalLength);

    if (isNaN(length) || length === 0) {
      data.totalLength = 0;
    } else {
      data.totalLength = length;
    }

    if (!data.optionMultiple) {
      data.orderCount = 1;
    }

    if (weight >= 1.0)
      data.totalWeight = parseFloat(String(weight).replace(/(^0+)/, ""));

    if (!validate(data)) {
      return;
    }

    try {
      const params = {
        departure: data.departure.addressBase,
        arrival: data.arrival.addressBase,
        vehicleType: data.vehicleType,
        vehicleWeight: data.vehicleWeight,
        totalLength: data.totalLength,
        optionMixedLoading: data.optionMixedLoading,
      };

      const orderService = new OrderService(navigate);
      const response = await orderService.estimate(params);

      data.estimatedDistance = response.data.distance;
      data.estimatedDuration = response.data.duration;
      data.estimatedCharge = response.data.charge;
    } catch (e) {
      console.error("failed to estimate distance and duration:", e);
    } finally {
      setOrder(data);
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      if (
        e.target.name === "requestNoteForDriver" ||
        e.target.name === "requestNoteForAdmin"
      )
        return;
      e.preventDefault(); // submit 이벤트를 막음
    }
  };

  const addWaypoint = (data: any) => {
    const waypoint = [];
    if (data?.waypoint0) waypoint.push(data.waypoint0);
    if (data?.waypoint1) waypoint.push(data.waypoint1);

    return waypoint;
  };

  const sendOrder = useCallback(
    async (data: any) => {
      try {
        setSendingOrder(true);

        const payload = {
          waypoints: addWaypoint(data),
          departureAddressBase: data.departure.addressBase,
          departureAddressDetail: data.departure.addressDetail,
          departureName: data.departure.name,
          departurePhone: data.departure.phone,
          departureDate: data.departure.date,

          arrivalAddressBase: data.arrival.addressBase,
          arrivalAddressDetail: data.arrival.addressDetail,
          arrivalName: data.arrival.name,
          arrivalPhone: data.arrival.phone,
          arrivalDate: data.arrival.date,

          vehicleType: data.vehicleType,
          vehicleWeight: data.vehicleWeight,

          item: data.item,
          totalWeight: data.totalWeight,
          totalLength: data.totalLength,
          packagingType: data.packagingType,
          loadingType: data.loadingType,
          unloadingType: data.unloadingType,

          requestNoteForAdmin: data.requestNoteForAdmin,
          requestNoteForDriver: data.requestNoteForDriver,

          optionShuttle: data.optionShuttle,
          optionMultiple: data.optionMultiple,
          optionAddingArrival: data.optionAddingArrival,
          optionMixedLoading: data.optionMixedLoading,
          optionCashOnDelivery: data.optionCashOnDelivery,
          optionBackDoorOpen: data.optionBackDoorOpen,

          orderCount: data.orderCount,
          orderInterval: data.orderInterval,

          estimatedCharge: data.estimatedCharge,
          estimatedDistance: data.estimatedDistance,
          estimatedDuration: data.estimatedDuration,
        };

        const orderService = new OrderService(navigate);
        await orderService.order(payload);

        window.gtag("event", `운송주문-확인모달-OFF`, {
          user_id: localStorage.getItem("customerName") || "none",
        });

        alert("운송주문이 완료되었습니다.");
        navigate(0);
      } catch (e: any) {
        setSendingOrder(false);
        alert(
          "현재 운송 주문이 안되는 상황입니다.\n불편을 끼쳐드려 죄송합니다. 고객센터나 운영자에게 연락주시면 바로 운송 주문을 도와드리겠습니다.\n(고객센터 : 02-6012-5100~1)\n\n아래 메시지를 포함하여 현재 화면을 캡쳐 후 운영자에게 전달 부탁드립니다. 문제를 해결하는데 많은 도움이 됩니다.\n(메시지 : " +
            e.toString() +
            " )"
        );
        console.error("failed to send order:", e);
      }
    },
    [setSendingOrder]
  );

  const getInfoBoxOrder = useCallback(async () => {
    try {
      const infoBoxService = new InfoBoxService(navigate);
      const response = await infoBoxService.getInfoBoxOrder();
      setInfoBox(response.data.data);
      setShowPopup(response.data.data.isOpen)
    } catch (e) {
      console.error("failed to handle info box:", e);
      }

  }, [setInfoBox, navigate]);

  useEffect(() => {
    queueMicrotask(getInfoBoxOrder);
  }, [getInfoBoxOrder])

  return (
    // FormProvider: 하위 form 들에 있는 값을 가지고 오기 위해 사용
    <>
      {/*for popup*/}
      <Popup infoBox={infoBox} close={() => setInfoBox({...infoBox, isOpen: false})} />
      <BusinessHoursPopup
        open={showBusinessHoursPopup}
        onClose={() => setShowBusinessHoursPopup(false)}
      />
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={handleKeyDown}
        >
          {!showBusinessHoursPopup && order && (
            <OrderCheck
              order={order}
              orderer={orderer}
              cancel={() => setOrder(null)}
              sendOrder={() => sendOrder(order)}
              sendingOrder={sendingOrder}
            />
          )}
          <Grid container direction={"row"}>
            <Grid item className={classes.left}>
              <Left />
            </Grid>
            <Grid item className={classes.right}>
              <Right
                setConfigIsCheckTotalWeight={setConfigIsCheckTotalWeight}
                configIsInputTotalLength={configIsInputTotalLength}
                setConfigIsInputTotalLength={setConfigIsInputTotalLength}
              />
            </Grid>
            <Grid item className={classes.sidebar}>
              <Sidebar />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  );
}
